/* The Organic Butchery — component-hero.js
   Hero › Full background video (also Page hero › Background video). The image
   underneath paints first (it is the LCP); the video is only fetched once the
   page is idle, then fades in when it actually plays. One MP4 per breakpoint
   (data-src-desktop / data-src-mobile); data-desktop-only skips phones.
   No autoplay with prefers-reduced-motion or Save-Data; the pause button is
   remembered for the session so the video stays paused on the next page. */
(() => {
  const KEY = 'tob-hero-video-paused';
  const store = {
    get: () => { try { return sessionStorage.getItem(KEY) === '1'; } catch (e) { return false; } },
    set: (on) => { try { on ? sessionStorage.setItem(KEY, '1') : sessionStorage.removeItem(KEY); } catch (e) {} },
  };
  const idle = (cb) => ('requestIdleCallback' in window ? requestIdleCallback(cb, { timeout: 2000 }) : setTimeout(cb, 800));
  const mobile = matchMedia('(max-width: 860px)');
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = Boolean(navigator.connection && navigator.connection.saveData);

  /* the script tag ships with each hero section, so it can run more than once */
  /* never fetch the hero video when the page is framed (previews, embeds) */
  if (window.self !== window.top) return;

  document.querySelectorAll('[data-hero-video]:not([data-bound])').forEach((video) => {
    video.setAttribute('data-bound', '');
    const hero = video.closest('.hero, [data-hero-root]');
    const toggle = hero && hero.querySelector('[data-hero-toggle]');
    const desktopOnly = video.hasAttribute('data-desktop-only');
    const srcFor = () => {
      const { srcDesktop: d, srcMobile: m } = video.dataset;
      if (mobile.matches) return m || (desktopOnly ? '' : d);
      return d || m;
    };

    const showState = (playing) => {
      if (!toggle) return;
      toggle.classList.toggle('is-paused', !playing);
      toggle.setAttribute('aria-pressed', String(!playing));
      toggle.setAttribute('aria-label', playing ? 'Pause background video' : 'Play background video');
    };
    const syncToggle = () => { if (toggle) toggle.hidden = !srcFor(); };
    /* desktop starts on the dark ground (see hero.liquid); bring the photo back
       whenever the video isn't going to play */
    const placeholders = hero ? [...hero.querySelectorAll('[data-video-placeholder]')] : [];
    const showPoster = () => { placeholders.forEach((s) => s.remove()); placeholders.length = 0; };
    /* progress ring: fills once per loop */
    let raf = 0;
    const tick = () => {
      if (toggle && video.duration) toggle.style.setProperty('--vid-p', (video.currentTime / video.duration).toFixed(4));
      raf = requestAnimationFrame(tick);
    };
    const play = () => {
      const src = srcFor();
      if (!src) return;
      if (video.getAttribute('src') !== src) video.setAttribute('src', src);
      const p = video.play();
      if (p && p.catch) p.catch(() => { showState(false); showPoster(); });
    };

    video.addEventListener('playing', () => {
      hero.classList.add('is-playing'); showState(true);
      cancelAnimationFrame(raf); raf = requestAnimationFrame(tick);
    });
    video.addEventListener('pause', () => { showState(false); cancelAnimationFrame(raf); });
    video.addEventListener('error', showPoster);

    if (saveData) { if (toggle) toggle.hidden = true; showPoster(); return; }
    syncToggle();

    if (reduce || store.get() || !srcFor()) { showState(false); showPoster(); }
    /* no photo painted on desktop: fetch the video straight away instead of waiting for idle */
    else if (placeholders.length && !mobile.matches) { video.preload = 'auto'; play(); }
    else idle(play);

    if (toggle) {
      toggle.addEventListener('click', () => {
        if (video.paused) { store.set(false); play(); }
        else { store.set(true); video.pause(); }
      });
    }
    /* rotating a tablet or resizing across the breakpoint swaps the source */
    mobile.addEventListener('change', () => {
      syncToggle();
      if (!srcFor()) { video.pause(); hero.classList.remove('is-playing'); showPoster(); }
      else if (!video.paused || (!video.getAttribute('src') && !reduce && !store.get())) play();
    });
  });
})();
